import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import React, { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { partnersQuotes } from 'rails_routes'
import { FileList, FileUpload, IconButton } from 'slab'
import { CENSUS_TEMPLATE_PATH } from 'utils'
import { getGetQuotingProspectsQueryKey, usePostQuotingProspects } from 'utils/web_api'
import type { QuotingProspect } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { FormCheckboxField, FormDateField, FormIntegerField, FormTextField } from '../reusable/form/FormFields'
import { StateDropdown } from '../reusable/form/StateDropdown'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet'

type ProspectFormData = QuotingProspect & {
  state: string
}

interface RequestAQuotePanelProps {
  triggerChildren: React.ReactNode
}

const getDefaultValues = (): Partial<ProspectFormData> => {
  return {
    name: '',
    employee_count: undefined,
    city: '',
    state: undefined,
    offers_health_insurance_today: undefined,
    health_insurance_effective_date: undefined,
    broker_uploads: [],
  }
}

const getFormDataFromProspectForm = (data: ProspectFormData) => {
  const formData = new FormData()
  if (data.name) {
    formData.append('name', data.name)
  }
  if (data.employee_count) {
    formData.append('employee_count', data.employee_count.toString())
  }
  if (data.city) {
    formData.append('city', data.city)
  }
  if (data.state) {
    formData.append('state', data.state)
  }
  if (data.offers_health_insurance_today) {
    formData.append('offers_health_insurance_today', data.offers_health_insurance_today.toString())
  }
  if (data.health_insurance_effective_date) {
    formData.append('health_insurance_effective_date', data.health_insurance_effective_date.toString())
  }
  if (data.broker_uploads) {
    const filesArray = Array.from(data.broker_uploads)
    filesArray.forEach((file) => {
      if (file instanceof File) {
        formData.append('broker_uploads[]', file)
      }
    })
  }
  formData.append('notify_sales', 'true')
  formData.append('create_sfdc_records', 'true')
  return formData
}

function useQuoteSubmission () {
  const { assign } = useWindowLocation()
  const [submitError, setSubmitError] = useState<GrapeAPIErrorType | null>(null)
  const bodyRef = React.useRef<FormData | null>(null)
  const lastDataRef = React.useRef<ProspectFormData | null>(null)
  const queryClient = useQueryClient()

  const mutation = usePostQuotingProspects({
    mutation: {
      onSuccess: async () => {
        try {
          await queryClient.invalidateQueries({
            queryKey: getGetQuotingProspectsQueryKey(),
          })
        } catch {}
        assign(partnersQuotes.index({ query: { quote_submitted: 'true' } }))
      },
      onError: (error: any) => {
        setSubmitError(error)
        window.segment?.track('[TfB] Quote request failed', { error: JSON.stringify(error?.message), owner_id: lastDataRef.current?.owner_id })
      },
    },
    request: {
      get body () {
        return bodyRef.current
      },
    },
  }, queryClient)

  const submitQuote: SubmitHandler<ProspectFormData> = async (data) => {
    window.segment?.track('[TfB] Quote request submitted', data)
    setSubmitError(null)

    const formData = getFormDataFromProspectForm(data)

    lastDataRef.current = data
    bodyRef.current = formData

    mutation.mutate()
  }

  const isSubmitting = mutation.isPending

  return { isSubmitting, submitError, submitQuote }
}

const shouldRenderSizeLimitError = (error: GrapeAPIErrorType): boolean => {
  if (!error?.errors || !Array.isArray(error.errors)) {
    return false
  }

  return error.errors.some(errorMessage =>
    typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('file size')
  )
}

const getErrorMessage = (error: GrapeAPIErrorType): string => {
  if (shouldRenderSizeLimitError(error)) {
    return 'The file you uploaded is too large. Please upload a file smaller than 20MB.'
  }
  if (error.status === 401) {
    return 'Your session has expired. Please refresh the page and sign in again to continue.'
  }
  return 'There was an error generating this quote. Please contact support@thatch.com for help.'
}

const ErrorBanner = React.forwardRef<HTMLDivElement, { error: GrapeAPIErrorType }>(
  ({ error }, ref) => {
    if (!error) { return }

    const errorMessage = getErrorMessage(error)

    return (
      <div
        ref={ref}
        className='bg-red-100 border border-red-400 text-red-700 p-2 my-2 rounded relative text-center text-xs'
        role='alert'
      >
        {errorMessage}
      </div>
    )
  }

)

const RequestAQuotePanel = ({ triggerChildren }: RequestAQuotePanelProps) => {
  const { isSubmitting, submitError, submitQuote } = useQuoteSubmission()
  const errorRef = React.useRef<HTMLDivElement>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const addAttachmentInputRef = React.useRef<HTMLInputElement>(null)
  const allowedFileTypes = ['.csv', '.xls', '.xlsx', '.pdf', '.bmp', '.jpg', '.jpeg', '.png', '.gif', '.tiff', '.tif', '.webp']

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProspectFormData>({
    defaultValues: getDefaultValues(),
  })

  const brokerUploadsRegister = register('broker_uploads', {
    required: 'File attachments are required'
  })

  const combinedFileUploadRef = React.useCallback((element: HTMLInputElement | null) => {
    Object.assign(fileInputRef, { current: element })
    brokerUploadsRegister.ref(element)
  }, [brokerUploadsRegister.ref])

  const handleFileChange = brokerUploadsRegister.onChange
  const handleFileBlur = brokerUploadsRegister.onBlur

  const showInsuranceStartDateField = watch('offers_health_insurance_today')
  const selectedBrokerUploads = watch('broker_uploads')

  const brokerUploadsArray = selectedBrokerUploads
    ? Array.from(selectedBrokerUploads)
    : []

  const handleRemoveFileAtIndex = (indexToRemove: number) => {
    const updatedFiles = brokerUploadsArray.filter((_, index) => index !== indexToRemove)

    const dataTransfer = new DataTransfer()
    updatedFiles.forEach(file => {
      if (file instanceof File) {
        dataTransfer.items.add(file)
      }
    })

    setValue('broker_uploads', dataTransfer.files as any)

    if (fileInputRef.current) {
      fileInputRef.current.files = dataTransfer.files
    }
  }

  const handleAddAttachment = () => {
    addAttachmentInputRef.current?.click()
  }

  const handleAddAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files
    if (!newFiles || newFiles.length === 0) return

    const existingFilenames = new Set(
      brokerUploadsArray.map(file => {
        return (file as any).filename || (file as any).name || ''
      })
    )

    const dataTransfer = new DataTransfer()

    brokerUploadsArray.forEach(file => {
      if (file instanceof File) {
        dataTransfer.items.add(file)
      }
    })

    Array.from(newFiles).forEach(file => {
      if (!existingFilenames.has(file.name)) {
        dataTransfer.items.add(file)
      }
    })

    setValue('broker_uploads', dataTransfer.files as any)

    if (fileInputRef.current) {
      fileInputRef.current.files = dataTransfer.files
    }

    if (addAttachmentInputRef.current) {
      addAttachmentInputRef.current.value = ''
    }
  }

  // we have been seeing bugs related to the submit button not working
  // when the modal is closed and then reopened, because the
  // Sheet thinks there are no files uploaded. This might be fixed
  // by moving to slab, but until then, we'll reset the form manually to ensure
  // no silent errors.
  const resetForm = () => {
    setValue('broker_uploads', [] as any)

    if (fileInputRef.current) {
      fileInputRef.current.files = null
    }
    if (addAttachmentInputRef.current) {
      addAttachmentInputRef.current.value = ''
    }
  }

  React.useEffect(() => {
    if (submitError && errorRef.current && errorRef.current.scrollIntoView) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [submitError])

  return (
    <Sheet onOpenChange={(open) => {
      if (open) {
        resetForm()
      }
    }}
    >
      <SheetTrigger asChild>
        {triggerChildren}
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            Request a quote
          </SheetTitle>
          <SheetDescription className='pt-4 pb-8'>
            Enter your client's business info. We'll follow up with you via email with the requested quote.
          </SheetDescription>
        </SheetHeader>
        {submitError && <ErrorBanner ref={errorRef} error={submitError} />}
        <form id='request-a-quote-form' onSubmit={handleSubmit(submitQuote)} encType='multipart/form-data'>
          <div className='flex flex-col pt-6 pb-10 gap-y-4'>
            <FormTextField<ProspectFormData>
              disabled={isSubmitting}
              error={errors.name}
              fieldId='name'
              fieldLabel='Business name'
              register={register}
              validationRule={{ required: 'Business name is required' }}
            />
            <FormIntegerField<ProspectFormData>
              disabled={isSubmitting}
              error={errors.employee_count}
              fieldId='employee_count'
              fieldLabel='Number of employees'
              register={register}
              validationRule={{ required: 'Number of employees is required' }}
            />
            <FormTextField<ProspectFormData>
              disabled={isSubmitting}
              error={errors.city}
              fieldId='city'
              fieldLabel='City'
              register={register}
              validationRule={{ required: 'City is required' }}
            />
            <StateDropdown<ProspectFormData>
              disabled={isSubmitting}
              error={errors.state}
              register={register}
              required
            />
            <FormCheckboxField<ProspectFormData>
              disabled={isSubmitting}
              error={errors.offers_health_insurance_today}
              fieldId='offers_health_insurance_today'
              fieldLabel='Does this business currently offer health insurance?'
              register={register}
            />
            {showInsuranceStartDateField && (
              <FormDateField<ProspectFormData>
                disabled={isSubmitting}
                error={errors.health_insurance_effective_date}
                fieldId='health_insurance_effective_date'
                fieldLabel='Health insurance effective date'
                register={register}
              />
            )}
            <div data-testid='file-upload-container' style={{ display: brokerUploadsArray.length > 0 ? 'none' : 'block' }}>
              <FileUpload
                name={brokerUploadsRegister.name}
                onChange={handleFileChange}
                onBlur={handleFileBlur}
                ref={combinedFileUploadRef}
                accept={allowedFileTypes.join(',')}
                label='Upload attachments'
                multiple
                description={
                  <>
                    Upload your employee's census, SBCs, rate data, and any other relevant docs here. Not sure what to include? Use our{' '}
                    <a href={CENSUS_TEMPLATE_PATH} target='_blank' rel='noopener noreferrer'>
                      census template
                    </a> for the fastest possible quote.
                  </>
                  }
                error={errors.broker_uploads?.message}
                disabled={isSubmitting}
              >
                <FileUpload.Content />
              </FileUpload>
            </div>
            {brokerUploadsArray.length > 0 && (
              <div>
                <label className='label-default font-medium text-ds-text-secondary mb-2'>
                  Uploaded attachments
                </label>
                <FileList aria-label='Uploaded census files'>
                  {brokerUploadsArray.map((file, index) => {
                    // Handle both File objects (during upload) and API response objects
                    const fileName = (file as any).filename || (file as any).name || 'Unknown file'
                    const fileSize = (file as any).byte_size || (file as any).size
                    return (
                      <FileList.File
                        key={`${fileName}-${index}`}
                        name={fileName}
                        size={fileSize}
                        status='success'
                        actions={
                          <IconButton
                            icon={Trash2}
                            label={`Remove ${fileName}`}
                            onClick={() => handleRemoveFileAtIndex(index)}
                            variant='default'
                          />
                          }
                      />
                    )
                  })}
                </FileList>
                {/* A hidden input is needed to support adding more files if some have already been added, since
                    using the same input will rewrite all files that have been added */}
                <input
                  ref={addAttachmentInputRef}
                  type='file'
                  multiple
                  accept={allowedFileTypes.join(',')}
                  onChange={handleAddAttachmentChange}
                  style={{ display: 'none' }}
                  disabled={isSubmitting}
                />
                {brokerUploadsArray.length < 20 && (
                  <button
                    type='button'
                    onClick={handleAddAttachment}
                    className='btn-secondary mt-3'
                    disabled={isSubmitting}
                  >
                    Add attachment
                  </button>
                )}
                <div className='body-sm text-gray-500 mt-3'>
                  Upload your employee's census, SBCs, rate data, and any other relevant docs here. Not sure what to include? Use our{' '}
                  <a href={CENSUS_TEMPLATE_PATH} target='_blank' rel='noopener noreferrer'>
                    census template
                  </a> for the fastest possible quote.
                </div>
              </div>
            )}
          </div>
          <footer>
            <button
              type='submit'
              className='btn-primary w-auto'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default RequestAQuotePanel
